/**
 * `GET|PUT /v1/cart`, `POST /v1/cart/items`, `PATCH|DELETE /v1/cart/items/{itemId}`,
 * `POST /v1/cart/merge` e `POST|DELETE /v1/cart/coupons[/{code}]`.
 *
 * Fontes: `CONTRACTS §8.2` (bloco "Carrinho": 6 linhas de rota, 8 operacoes), `API §6.9`
 * (a regra canonica do carrinho hibrido), `API §6.20.5` (o corpo de `POST /v1/cart/items`),
 * `API §4.2` (lock otimista), `DOMAIN §4.1` (o agregado, as 10 invariantes e os 8 passos do
 * merge) e `PWA.md §8.1/§8.2/§8.4` (espelho local e fila offline).
 *
 * ## A decisao que governa este arquivo inteiro: **o carrinho nao carrega dinheiro**
 *
 * Nenhum schema daqui tem `Money`, `Cents`, `unitPrice`, `lineTotal`, `discount` ou `totals`.
 * Nao e omissao, e a fronteira:
 *
 * > `PWA.md §8.1` — "O carrinho **nunca guarda preco em nenhum dos dois lados**: toda exibicao
 * > de valor, toda validacao regulatoria e toda checagem de disponibilidade vem de uma cotacao
 * > do servidor (`POST /v1/checkout/quote`)". `API §6.9` repete: "a deteccao de mudanca de
 * > preco e do `pricingSnapshotId` — **nunca de preco gravado em item**".
 *
 * O schema Prisma ja aplicou a mesma decisao no banco (`cart_item` perdeu
 * `unitPriceCentsSeen`, X-38). Aqui ela sobe para o contrato: se o tipo nao tem campo de
 * dinheiro, nenhuma tela consegue somar carrinho sem passar pela cotacao — e a briga de balcao
 * que o produto existe para evitar nao tem por onde nascer.
 *
 * **DUVIDA (divergencia declarada e reportada).** `API §6.20.5` desenha a resposta de
 * `POST /v1/cart/items` **com** `unitPrice: ChargedPrice`, `lineTotal`, `appliedPromotions` e
 * `totals`. Este contrato nao os tem. A precedencia decide (`CONTRACTS §1.1`:
 * `CONTRACTS.md` > `DOMAIN.md` > `API.md`): `CONTRACTS §7.2` fixa o carrinho hibrido de X-38 e
 * `PWA.md §8.1` — que e a outra ponta da mesma regra — proibe preco nos dois lados. Somar
 * carrinho em `POST /v1/cart/items` significaria rodar as 11 etapas da cascata (`CONTRACTS §3.1`)
 * a cada toque em "+", com TTL de 10 min (`CONTRACTS §6`) que a resposta nao tem como declarar.
 * O que este contrato mantem de `§6.20.5` e tudo o que **nao** e dinheiro: `version`,
 * `pricingSnapshotId`, `maxOrderableQty`, `availability`, o bloco `regulatory` e os `warnings`.
 *
 * ## O que tambem NAO aparece
 *
 * - **Numero absoluto de estoque.** Nunca. So `AvailabilityBand` (`CONTRACTS §2.13`, RN-063,
 *   X-22). `maxOrderableQty` e teto de compra, nao saldo: ele ja e o minimo entre o limite
 *   sanitario, o teto do produto e o que a filial consegue separar.
 * - **Classe controlada.** Um item `CTRL_*` nao chega a existir nesta superficie (REG-002,
 *   INV-C3): ele nao entra no carrinho, e sai do carrinho existente se a instancia deixar de
 *   vender a classe.
 */
import { z } from 'zod';

import { AvailabilityBandSchema, RegulatoryClassSchema } from '../enums';
import { ErrorCodeSchema } from '../errors';
import { InstantSchema, OptimisticVersionSchema, QuantitySchema, Uuidv7Schema } from '../common';

// ---------------------------------------------------------------------------
// Cabecalhos, cookie e cache
// ---------------------------------------------------------------------------

/**
 * `API §8.1`, linha "`GET /v1/cart`": `private, no-store`, sem CDN, com `ETag` — e o `ETag`
 * serve para `If-Match`, **nao** para cache.
 *
 * Fica no contrato, e nao numa constante do controller, pelo mesmo motivo de
 * `PUBLIC_STORES_CACHE_CONTROL`: o valor e verificavel de fora, e o teste de integracao o
 * compara literalmente. Toda rota de carrinho responde este valor — inclusive as de mutacao,
 * inclusive as que devolvem erro.
 */
export const CART_CACHE_CONTROL = 'private, no-store';

/**
 * Cookie do carrinho anonimo — `API §6.9`: "`cartToken` opaco em cookie `HttpOnly`, TTL 7 dias".
 *
 * O prefixo `__Host-` obriga `Secure`, `Path=/` e a ausencia de `Domain` — a mesma trava que
 * `__Host-session` usa para impedir que um subdominio irmao comprometido sobrescreva o cookie
 * (AMZ-003). Ele **nao e credencial de identidade**: nao autentica ninguem, so aponta para um
 * carrinho anonimo. Por isso ele convive com a sessao em vez de substitui-la.
 */
export const CART_TOKEN_COOKIE_NAME = '__Host-cart';

/** TTL do carrinho anonimo, em dias (`CONTRACTS §7.2`, `API §6.9`). */
export const CART_TTL_ANONYMOUS_DAYS = 7;
/** TTL do carrinho do cliente autenticado, em dias (`CONTRACTS §7.2`, DOMAIN §4.1). */
export const CART_TTL_CUSTOMER_DAYS = 30;

// ---------------------------------------------------------------------------
// Primitivos
// ---------------------------------------------------------------------------

/**
 * Codigo de cupom digitado pelo cliente (`coupon.code`, `Citext @unique`).
 *
 * Normalizado para maiuscula no schema porque a coluna e case-insensitive e o cliente digita
 * como quiser; sem a normalizacao, `bemvindo10` e `BEMVINDO10` viram dois `couponCode`
 * diferentes em `cart` e a comparacao no checkout deixa de ser uma igualdade.
 */
export const CouponCodeSchema = z
  .string()
  .trim()
  .min(3)
  .max(32)
  .transform((code) => code.toUpperCase())
  .pipe(
    z
      .string()
      .regex(/^[A-Z0-9][A-Z0-9-]{1,30}[A-Z0-9]$/, 'Código de cupom inválido (DATA_MODEL §3.6).'),
  );
export type CouponCode = z.infer<typeof CouponCodeSchema>;

/** `FulfillmentMode` do carrinho — uma modalidade por carrinho (INV-C2). */
export const CartFulfillmentModeSchema = z.enum(['DELIVERY', 'PICKUP']);
export type CartFulfillmentMode = z.infer<typeof CartFulfillmentModeSchema>;

/**
 * Teto duro de quantidade **do protocolo**, nao da regra.
 *
 * O teto que vale e o regulatorio (REG-013, `maxQuantityPerOrder`) e o da disponibilidade
 * (`maxOrderableQty`), e os dois sao decididos no servidor. Este numero existe so para que um
 * `quantity: 1e9` seja recusado como `VALIDATION_ERROR` antes de virar consulta — recusar lixo
 * na borda e mais barato que recusa-lo no dominio, e nenhum dos dois substitui o outro.
 */
export const CART_ITEM_QUANTITY_MAX = 999;

export const CartQuantitySchema = QuantitySchema.max(CART_ITEM_QUANTITY_MAX);

// ---------------------------------------------------------------------------
// Requisicoes
// ---------------------------------------------------------------------------

/**
 * `POST /v1/cart/items` — `API §6.20.5`, com o corpo **exatamente** como o documento o escreve.
 *
 * ## `storeId` e OPCIONAL desde D-33, e o que mudou foi quem escolhe
 *
 * Este paragrafo dizia que `storeId` e "redundante de proposito", e que a redundancia
 * transforma *"o cliente trocou de loja em outra aba"* de bug silencioso em erro nomeado. A
 * defesa continua de pe — `409 CART_STORE_MISMATCH` no ramo do carrinho existente —, mas o
 * cenario que ela protegia **deixou de existir**: com D-33 o cliente nao tem seletor de filial,
 * entao nao ha aba onde trocar de loja.
 *
 * Ausente, o servidor RESOLVE a filial por "tem o item e esta aberta"
 * (`escolherFilialQueAtende`, `@farmacia/domain`). Presente, ele obedece — e e assim que
 * `POST /v1/store/orders` (pedido assistido, D9) continua nomeando a filial do balconista, que
 * sabe em qual loja esta.
 *
 * **`.optional()` e nao remocao**, e a diferenca importa por causa do `.strict()` logo abaixo:
 * campo desconhecido e ERRO, entao apagar `storeId` do schema transformaria em `422` todo
 * cliente ainda em campo que o envie — inclusive o PWA que o service worker de alguem serviu
 * do cache. Opcional aceita os dois, e e o que permite migrar a API antes do front.
 *
 * `.strict()`: **campo desconhecido no corpo e `VALIDATION_ERROR`, nao campo ignorado.**
 * Essa e a linha que faz `SEC-06`/`SEC-10` serem verificaveis — um corpo com
 * `unitPriceCents`, `discountCents` ou `totalCents` nao "passa despercebido", ele e recusado
 * pela borda. Ignorar em silencio tambem seria seguro, mas seria indistinguivel de um servidor
 * que aceitou o campo e o perdeu.
 */
export const AddCartItemRequestSchema = z
  .object({
    productId: Uuidv7Schema,
    quantity: CartQuantitySchema,
    /** Ausente ⇒ o servidor resolve (D-33). Ver o cabecalho. */
    storeId: Uuidv7Schema.optional(),
  })
  .strict();
export type AddCartItemRequest = z.infer<typeof AddCartItemRequestSchema>;

/** `PATCH /v1/cart/items/{itemId}` — so quantidade (`API §6.9`, "Quantidade"). */
export const PatchCartItemRequestSchema = z.object({ quantity: CartQuantitySchema }).strict();
export type PatchCartItemRequest = z.infer<typeof PatchCartItemRequestSchema>;

/** Uma linha de intencao no espelho local (`PWA.md §8.1`, `LocalCartLine` — sem preco). */
export const CartItemIntentSchema = z
  .object({
    productId: Uuidv7Schema,
    quantity: CartQuantitySchema,
  })
  .strict();
export type CartItemIntent = z.infer<typeof CartItemIntentSchema>;

/**
 * `PUT /v1/cart` — a **sincronizacao do espelho local** da fila offline do PWA
 * (`API §6.9`, `PWA.md §8.1(b)` e `§8.4`, linha `CART_MIRROR`).
 *
 * Substituicao **total** da lista de intencao: o que nao vier no corpo deixa de existir. E o
 * que permite que a mesma rota sirva de "esvaziar carrinho" (lista vazia) e de "trocar de
 * filial" (`storeId` diferente) no MVP — as duas rotas `P` que `CONTRACTS §8.2` adia
 * (`DELETE /v1/cart` e `PUT /v1/cart/store`) dependem exatamente disto.
 *
 * `updatedAt` e o carimbo **do cliente**, e existe para o desempate de conteudo descrito em
 * `PWA.md §8.1`: ultimo-escreve-vence por `updatedAt`. Ele nao substitui o `If-Match` — ver
 * `ReplaceCartRequest` no controller e a tabela de precedencia do modulo.
 */
export const ReplaceCartRequestSchema = z
  .object({
    storeId: Uuidv7Schema,
    /** Quando o espelho local foi editado pela ultima vez, no relogio do dispositivo. */
    updatedAt: InstantSchema,
    fulfillmentMode: CartFulfillmentModeSchema.optional(),
    items: z.array(CartItemIntentSchema).max(200),
  })
  .strict();
export type ReplaceCartRequest = z.infer<typeof ReplaceCartRequestSchema>;

/**
 * `POST /v1/cart/merge` — funde o carrinho anonimo ao logar (`API §6.9`, DOMAIN §4.1).
 *
 * O corpo e **opcional** e carrega o espelho local (`PWA.md §8.2`: "`POST /v1/cart/merge` com
 * o carrinho local"). O carrinho anonimo do SERVIDOR nao vem no corpo: ele e encontrado pelo
 * cookie `__Host-cart`, porque um `cartToken` no corpo seria um identificador de carrinho
 * alheio que qualquer um poderia tentar adivinhar.
 */
export const MergeCartRequestSchema = z
  .object({
    storeId: Uuidv7Schema.optional(),
    updatedAt: InstantSchema.optional(),
    items: z.array(CartItemIntentSchema).max(200).optional(),
  })
  .strict();
export type MergeCartRequest = z.infer<typeof MergeCartRequestSchema>;

/**
 * `POST /v1/cart/coupons` — `{ code }`, e nada mais (`API §6.11`).
 *
 * `.strict()` aqui e o controle de `SEC-10`: o cliente manda o **codigo**, nunca o valor. Um
 * corpo com `discountCents` e recusado, e o teste de seguranca prova isso.
 */
export const ApplyCouponRequestSchema = z.object({ code: CouponCodeSchema }).strict();
export type ApplyCouponRequest = z.infer<typeof ApplyCouponRequestSchema>;

/** Parametro de caminho de `DELETE /v1/cart/coupons/{code}`. */
export const CouponCodeParamSchema = z.object({ code: CouponCodeSchema });
export type CouponCodeParam = z.infer<typeof CouponCodeParamSchema>;

/** Parametro de caminho das rotas de item. */
export const CartItemIdParamSchema = z.object({ itemId: Uuidv7Schema });
export type CartItemIdParam = z.infer<typeof CartItemIdParamSchema>;

// ---------------------------------------------------------------------------
// Resposta
// ---------------------------------------------------------------------------

/**
 * Aviso que **nao e erro** (`API §6.20.5`: "quantidade ajustada, item degradado para
 * `ON_REQUEST`, loja movimentada").
 *
 * `code` e o catalogo FECHADO de `@farmacia/contracts` — nao ha string livre. Um aviso com
 * codigo inventado seria um codigo que nenhuma UI sabe tratar e que o gate do `API §10.3`
 * reprova (`CONTRACTS §7.3`).
 */
export const CartWarningSchema = z.object({
  code: ErrorCodeSchema,
  message: z.string().min(1),
  /** O item afetado, quando o aviso e de linha. `null` quando e do carrinho inteiro. */
  itemId: Uuidv7Schema.nullable(),
  /**
   * O produto afetado. Extensao ao `{code, message, itemId?}` de `API §6.20.5`, e ela existe
   * por um caso concreto: um item recusado que **nunca chegou a ter linha** (veio do espelho
   * local ou do carrinho anonimo) nao tem `itemId`, e sem `productId` a folha "Algumas coisas
   * mudaram" de `PWA §8.5` nao conseguiria dizer qual item sumiu.
   *
   * A mensagem continua **sem nome de medicamento** (REG-044): o nome so aparece em `items[]`,
   * e o cliente resolve o `productId` contra o proprio espelho local.
   */
  productId: Uuidv7Schema.nullable(),
});
export type CartWarning = z.infer<typeof CartWarningSchema>;

/** O bloco regulatorio da linha — copiado na inclusao e **revalidado** a cada leitura (DOMAIN §4.1). */
export const CartItemRegulatorySchema = z.object({
  class: RegulatoryClassSchema,
  requiresPrescription: z.boolean(),
  requiresRetention: z.boolean(),
});
export type CartItemRegulatory = z.infer<typeof CartItemRegulatorySchema>;

export const CartItemSchema = z.object({
  id: Uuidv7Schema,
  productId: Uuidv7Schema,
  displayName: z.string().min(1),
  imageUrl: z.string().nullable(),
  quantity: CartQuantitySchema,
  /**
   * Teto de compra desta linha nesta filial. **Nao e saldo** — e o minimo entre o teto
   * sanitario (REG-013), o teto do produto (`product.maxQtyPerOrder`) e o que a filial
   * consegue separar (`store_product_offer.maxOrderableQty`, que ja desconta buffer de
   * seguranca e reservas). O cliente nunca ve `onHandQty` (RN-063, X-22).
   */
  maxOrderableQty: z.number().int().nonnegative(),
  availability: AvailabilityBandSchema,
  regulatory: CartItemRegulatorySchema,
});
export type CartItem = z.infer<typeof CartItemSchema>;

/**
 * O carrinho inteiro — `API §6.20.5`: "devolve o carrinho inteiro, nao so o item. Em 3G, um
 * round-trip vale mais que bytes". Toda rota deste modulo, inclusive `DELETE`, devolve isto.
 */
export const CartResponseSchema = z.object({
  id: Uuidv7Schema,
  /** Base do `ETag` fraco `W/"cart-<id>-<version>"` (`CONTRACTS §7.2`, `API §4.2`). */
  version: OptimisticVersionSchema,
  /**
   * Muda quando o sync do ERP altera preco de item do carrinho — **separado** de `version`
   * para que o sync nao gere conflito falso de `If-Match` (`API §4.2`). O carrinho nao sabe
   * qual foi a mudanca: quem compara e `POST /v1/checkout/quote`.
   */
  pricingSnapshotId: Uuidv7Schema,
  /** Filial travada: uma filial dispensa, uma filial fatura (INV-C1). */
  storeId: Uuidv7Schema,
  fulfillmentMode: CartFulfillmentModeSchema,
  /** A **intencao** de cupom. Validade, elegibilidade e valor sao da cotacao (INV-C8). */
  couponCode: CouponCodeSchema.nullable(),
  items: z.array(CartItemSchema),
  warnings: z.array(CartWarningSchema),
  /** `true` se alguma linha exige receita — a UI antecipa o aviso antes do checkout. */
  requiresPrescription: z.boolean(),
  /** Anonimo 7 dias, logado 30, contados de `updatedAt` (DOMAIN §4.1, `CONTRACTS §7.2`). */
  expiresAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type CartResponse = z.infer<typeof CartResponseSchema>;

/**
 * O corpo do `409 CART_STORE_MISMATCH` do merge — vai em `meta` do problem details.
 *
 * DOMAIN §4.1, passo 2: filiais divergentes **nao sao decididas pelo servidor**; ele "devolve
 * os dois resumos e o cliente escolhe". A alternativa descartada esta no proprio documento:
 * adotar a filial do carrinho logado, "descartada porque o cliente montou o carrinho anonimo
 * olhando o preco e o estoque da outra loja".
 */
export const CartStoreMismatchMetaSchema = z.object({
  current: z.object({ storeId: Uuidv7Schema, itemCount: z.number().int().nonnegative() }),
  incoming: z.object({ storeId: Uuidv7Schema, itemCount: z.number().int().nonnegative() }),
});
export type CartStoreMismatchMeta = z.infer<typeof CartStoreMismatchMetaSchema>;

/**
 * O `meta` do **`409 CART_VERSION_CONFLICT`** — o carrinho como ele esta agora.
 *
 * `API §4.2` e literal sobre o motivo de o corpo do conflito trazer a representacao inteira:
 * *"Assim o cliente resolve o conflito sem um segundo `GET` (importante em 3G)"*. Sem um
 * schema para isso, todo consumidor faria `meta.current as CartResponse` — um `as` sobre dado
 * que veio da rede, que e onde a tipagem para de valer.
 *
 * `current` e **anulavel** porque `CartWorkspace.versionConflict` devolve `null` quando o
 * carrinho nao existe mais para aquele dono (sessao trocada, TTL vencido, merge que aposentou
 * o carrinho anonimo). Nesse caso o cliente esquece a versao que guardava, e nao "recarrega
 * um carrinho vazio".
 */
export const CartVersionConflictMetaSchema = z.object({
  current: CartResponseSchema.nullable(),
});
export type CartVersionConflictMeta = z.infer<typeof CartVersionConflictMetaSchema>;
